// Single source of truth for the guide's content. FeatureGuide renders FEATURES; the Help
// Center search builds its index from the same data (plus the non-feature sections) so the
// two can never drift. Ids match the anchors rendered on the page and the jump links in
// GuideContents, so a search result can scroll straight to its section.
//
// Feature GIFs (drop files into public/guide/ to activate; see GuideMedia):
//   Each feature shows exactly one GIF, from public/guide/<feature-id>.gif, displayed under
//   the feature summary. Override a feature with `media: '<clip-id>'`, or opt out with
//   `media: false`. Missing files self-hide, so a feature whose GIF is not added yet renders
//   as before.

export const FEATURES = [
  {
    id: 'seller-profile',
    tag: 'Foundation',
    name: 'Your Seller Profile (ICP)',
    what:
      'Your Seller Profile is the definition of who you sell to and what you sell. It is the reference every hunt reads from. When you leave details out of a search, RepBuddy fills them in from this profile, so a strong profile is the difference between vague results and a list you can send today. You set it once, it saves to the cloud, and it follows you across devices and browsers.',
    steps: [
      'Open Seller Profile from the app sidebar.',
      'Enter who you are: seller name, company, title, and a one-line value proposition that says what you help buyers do.',
      'Define your ideal customer: target tech verticals and traditional industries, company size by employees, and annual revenue range.',
      'Narrow the target with geography and exclude keywords so the wrong kinds of companies never show up.',
      'Set your voice: tone, preferred call to action, phrases that sound like you, and words you would never say. Drafting uses these.',
      'Save. The profile applies to every future hunt automatically.',
    ],
    tips: [
      'Be specific with titles and industries. "VP of Operations at logistics companies" returns far cleaner matches than "leaders at businesses."',
      'Use exclude keywords to remove recurring noise, for example agencies, students, or a segment you never sell to.',
      'As you run hunts, RepBuddy may suggest profile updates it has learned from your searches. Review each one and keep it or dismiss it. Nothing changes without your approval.',
    ],
    note:
      'Think of the profile as the brief you would give a new sales researcher on day one. The clearer the brief, the less you have to correct later.',
  },
  {
    id: 'contact-hunt',
    tag: 'Find people',
    name: 'Contact hunt',
    what:
      'A contact hunt is the core workflow. You describe the people you want in one sentence and RepBuddy returns real contacts that match. Before any contact reaches your list, its email is checked against the live mail server for deliverability and the person is confirmed to still work where the record says. Contacts that fail either check never reach you, and you are never charged for them.',
    steps: [
      'Start a new search and make sure you are in contact mode.',
      'Type your request in plain English, for example "Find me 20 Heads of Marketing at Series B SaaS companies in California."',
      'Before it runs, RepBuddy shows an estimate of the most lead credits the search could use and how many you have left.',
      'Let it work. The Hunter finds people, the Verifier proves each email is deliverable and each person still holds the role.',
      'Review the results table: name, title, company, and verified email for each contact.',
      'After it finishes, RepBuddy shows exactly how many credits were used and how many contacts were not charged because they had no verified email.',
    ],
    tips: [
      'State the count you want. "Find me 25" returns a fuller list than leaving the number open.',
      'You do not have to specify everything. Anything you omit is filled from your Seller Profile, so keep that profile sharp.',
      'RepBuddy tracks the contacts it has already delivered to you, so repeat hunts return net-new people instead of the same names again.',
      'If results skew wrong, add one constraint at a time, such as a tighter title or a region, rather than rewriting the whole request.',
    ],
  },
  {
    id: 'deep-hunt',
    tag: 'Find people',
    name: 'Company-first hunt (Deep Hunt)',
    what:
      'A Deep Hunt starts from the account instead of the person. Use it when the company matters more than the exact title. RepBuddy finds companies that match your ideal customer profile, vets each one, and then pulls the right one to five people at each company. You end up with a focused set of accounts and the correct people inside them.',
    steps: [
      'Start a new search and describe the kind of company you want to reach, plus the roles you want at each.',
      'RepBuddy assembles a list of matching companies and checks them against your profile.',
      'For each qualified company, it selects and verifies the most relevant contacts.',
      'Review the results grouped by company, with each contact verified the same way as a contact hunt.',
    ],
    tips: [
      'Reach for Deep Hunt when your strategy is account-based, for example targeting a defined list of industries or company profiles rather than a single job title everywhere.',
      'Keep the per-company role list short and specific. Asking for the one or two people who actually own the decision beats a wide net.',
    ],
  },
  {
    id: 'email-verification',
    tag: 'Find people',
    name: 'Email verification',
    what:
      'Email verification checks addresses you already have. Paste or upload an old list and RepBuddy sorts every address into valid, risky, or invalid. This is how you protect your sender reputation before a campaign, so you never burn a domain sending to dead inboxes. Verification runs against the live mail server, not a stale database.',
    steps: [
      'Bring the emails you want to check, either pasted in or uploaded.',
      'Run the verification.',
      'Read the results by bucket: valid addresses are safe to send, risky addresses may bounce, invalid addresses should be removed.',
      'Export the cleaned list and send only to the addresses you trust.',
    ],
    tips: [
      'Verify any list older than a few weeks before you send. People change jobs constantly and inboxes go dark.',
      'Verification draws on your email verification allowance, not your lead credits, so cleaning a list never spends the credits you save for net-new leads.',
    ],
  },
  {
    id: 'website-finder',
    tag: 'Find people',
    name: 'Website finder',
    what:
      'Website finder takes a list with company names only and finds each company\'s real, current website. It validates every match and flags companies that have shut down or been acquired, so you are not researching a business that no longer exists.',
    steps: [
      'Upload or paste your list of company names.',
      'Run the finder.',
      'Review each resolved website alongside its company and the source of the match.',
      'Use the validated sites as the starting point for enrichment or manual research.',
    ],
    tips: [
      'Run website finder before enrichment when your source data is just names. Clean domains make every downstream step more accurate.',
      'Pay attention to the flags. A company marked as closed or acquired is a signal to drop it or change your angle.',
    ],
  },
  {
    id: 'batch-search',
    tag: 'Find people',
    name: 'Batch search from a CSV',
    what:
      'Batch search takes a list of companies you already have and finds the right people at every one of them. Upload a CSV of company names, say which role you want, and RepBuddy works down the list company by company. Every contact goes through the same checks as a normal hunt: the email is proven deliverable and the person is confirmed to still hold the role. Reach for it when you already know the accounts and only need the people inside them.',
    steps: [
      'Upload a CSV of company names into the chat.',
      'Click Find Contacts, or type the role yourself if you would rather name it. If your Seller Profile has target titles saved, RepBuddy uses those automatically and tells you which ones it picked.',
      'Answer how many contacts you want per company, and whether to pull a fresh pool or reuse people you have already been given.',
      'RepBuddy resolves each company name to its real website before searching, so it looks at the right business. If a name is ambiguous, it asks you to pick the correct match.',
      'Watch results arrive company by company as the run progresses. You do not wait for the whole list to finish before you see anything.',
      'Download the finished list as a CSV when the run completes.',
    ],
    tips: [
      'Keep the per-company count low. Two or three of the right people at each account beats ten at one company and none at the rest.',
      'A batch does not have to finish in one go. If a run stops early, whatever it found is yours to download and you are only charged for those contacts. Use "Finish remaining" to pick up the companies it missed.',
      'Save your target titles in the Seller Profile before you run a batch. With them stored, the whole run starts from one click instead of you typing a role every time.',
      'Company names on their own are enough. You do not need to add domains to your file first, because each name is matched to its real website as part of the run.',
    ],
    note:
      'Batch search pairs well with website finder and list enrichment. Clean up and enrich a raw list of company names first, then run a batch over it to get the people.',
  },
  {
    id: 'list-enrichment',
    tag: 'Enrich and write',
    name: 'List enrichment',
    what:
      'List enrichment fills in the details behind a list of companies. Drop in a CSV, choose which fields you want, and RepBuddy returns your file with those columns populated. Your original columns are preserved and the new ones are appended, so nothing you had is lost. Available fields include head count, revenue, industry, website, HQ location, founded year, operating status, description, specialties, and LinkedIn URL.',
    steps: [
      'Switch to the enrichment view in the same window as your searches.',
      'Upload a CSV of companies and tell RepBuddy which column holds the company name.',
      'Select the fields you want to populate. Pick only what you need.',
      'Check the preview, which shows how each input name was matched, so you can catch a wrong match before it runs.',
      'Run the enrichment, then download the completed file as XLSX with your columns intact and the new data added.',
    ],
    tips: [
      'RepBuddy maps well-known companies to their exact domains to stop the common problem of matching the wrong business with a similar name. Still, scan the preview for any company where the name is ambiguous.',
      'Every enriched field carries a confidence tier: verified means checked and confirmed, researched means assembled by the engine from multiple sources, and partial means it is the best available and flagged as such. Treat partial fields as leads to confirm, not facts.',
      'Enrichment uses your company enrichment allowance. Some fields that require a website or LinkedIn lookup also draw on those meters, so select only the fields you will actually use.',
    ],
  },
  {
    id: 'ai-drafting',
    tag: 'Enrich and write',
    name: 'AI email drafting',
    what:
      'Any contact can become a researched, personalized opening email in one message. The draft is built from the context RepBuddy has on the person and their company, and it is shaped by the tone, call to action, and phrasing in your Seller Profile. You edit and revise it in the chat, conversationally, until it reads like something you would actually send.',
    steps: [
      'From a contact, ask RepBuddy to draft an opener.',
      'Read the first draft. It uses your voice settings and the company context already gathered.',
      'Revise in plain language, for example "make it shorter," "mention their recent funding," or "lead with the problem, not us."',
      'Copy the final version into your sequencer or send it directly from your own tools.',
    ],
    tips: [
      'The better your Seller Profile voice settings, the closer the first draft lands. Fill in the phrases you use and the words you would never say.',
      'Revise in small, specific instructions. One change at a time gives you tighter control than a full rewrite request.',
      'Drafting and chat never use credits. They are included free on every plan, so iterate as much as you want.',
    ],
  },
  {
    id: 'chat-sessions',
    tag: 'Manage your work',
    name: 'Chat sessions and history',
    what:
      'Every search is saved as a chat session in the sidebar. Sessions persist to the cloud, so they are still there after you log out and back in, and they follow you to any device or browser. You can start a new session at any time and reopen an old one to see its results again. Contact hunting and company enrichment live in the same window, and you switch between them with a mode toggle without losing work in progress.',
    steps: [
      'Click New search to start a fresh session whenever you change direction.',
      'Find past work in the sidebar, listed as sessions you can reopen.',
      'Reopen any session to view the contacts or enrichment it produced.',
      'Toggle between contact and enrichment modes in the same window to keep related work together.',
    ],
    tips: [
      'Use a new session per campaign or per target segment. It keeps results organized and easy to return to.',
      'Because history is stored in the cloud, you can start a hunt on your laptop and pick it up later on another machine.',
    ],
  },
  {
    id: 'exporting',
    tag: 'Manage your work',
    name: 'Exporting your results',
    what:
      'Nothing you find is locked inside RepBuddy. Export any set of contacts to a CSV with name, title, company, and email, ready to load into your CRM or sequencer. Enrichment results download as an XLSX file with your original columns preserved and the enriched fields added.',
    steps: [
      'Open the result you want to export, whether a contact hunt or an enrichment run.',
      'Use the export action to download the file.',
      'For contacts you get a CSV; for enrichment you get an XLSX with every column intact.',
      'Import the file into whatever tool you send from.',
    ],
    tips: [
      'Export contact lists right before you load a sequence, so you are always sending to the freshest verified data.',
      'Keep exports organized by session or campaign so your CRM imports stay clean and traceable.',
    ],
  },
];

// The non-feature sections of the page, so search covers the whole guide, not just the
// feature cards. Text is what search matches against; snippet is what a result shows.
const EXTRA_SECTIONS = [
  {
    id: 'getting-started',
    tag: 'Quick start',
    name: 'Getting started in five steps',
    snippet:
      'The shortest path from a new account to your first list of verified leads: create your account, fill in your Seller Profile, run a contact hunt, review the results, then export or draft outreach.',
    text:
      'getting started quick start onboarding new account free trial seven days thirty verified leads create account seller profile first contact hunt review results export draft outreach first steps setup',
  },
  {
    id: 'credits',
    tag: 'Credits',
    name: 'Credits and plan limits',
    snippet:
      'How metering works: you spend a lead credit only on a net-new lead that passes verification. Email verification, company enrichment, and website lookups draw on their own allowances. See the cost before and after every action.',
    text:
      'credits plan limits meters allowances lead credits email verifications company enrichments website lookups solo pro monthly boosters cost before after balance running low pricing unlimited ai drafting free billing',
  },
  {
    id: 'best-practices',
    tag: 'Best practices',
    name: 'Getting the best results',
    snippet:
      'The habits that get the most out of RepBuddy: invest in your Seller Profile, ask in plain language then refine, trust the checks, read the confidence tiers, organize by session, and verify before you send.',
    text:
      'best practices tips getting the best results habits seller profile plain language refine trust the checks confidence tiers verified researched partial organize sessions verify before you send data isolation privacy row level security support help',
  },
];

/**
 * Flat, searchable index of every guide section. Each entry carries the anchor id, a display
 * title, a short section tag, a one-line snippet for the result row, and a lowercased haystack
 * that concatenates every piece of the section's copy so a keyword anywhere in it will match.
 */
export const GUIDE_INDEX = [
  ...FEATURES.map((f) => ({
    id: f.id,
    title: f.name,
    tag: f.tag,
    snippet: f.what,
    text: [
      f.name,
      f.tag,
      f.what,
      ...(f.steps || []).map((s) => (typeof s === 'string' ? s : s.text)),
      ...(f.tips || []),
      f.note || '',
    ]
      .join(' ')
      .toLowerCase(),
  })),
  ...EXTRA_SECTIONS.map((s) => ({
    id: s.id,
    title: s.name,
    tag: s.tag,
    snippet: s.snippet,
    text: [s.name, s.tag, s.snippet, s.text].join(' ').toLowerCase(),
  })),
];

/** Return the guide sections whose text contains every whitespace-separated term in `query`. */
export function searchGuide(query) {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [];
  return GUIDE_INDEX.filter((entry) => terms.every((t) => entry.text.includes(t)));
}
